import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import AssetTable from '../components/Asset/AssetTable';
import AssetMap from '../components/AssetMap';
import AssetOverlay from '../components/Asset/AssetOverlay';
import { getInventory, deleteAsset, getAssetStats } from '../store/actions/assetActions';
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import { LuPlus, LuBox, LuDollarSign, LuSearch } from "react-icons/lu";
import { CheckCircle, AlertCircle, Box, DollarSign, Search, Plus } from "lucide-react";
import AssetMetric from "../components/Charts/General/AssetMetric"
import AssetStatusMetric from "../components/Charts/General/AssetStatusMetric"
import AssetQuarterMetric from "../components/Charts/General/AssetQuarterMetric"
import TopAssets from "../components/Charts/General/TopAssets"

const ITInventory = ({ assets, loading, error, getInventoryFN, deleteAssetFN, sidebar_collapsed, activeassetforworkflow, setActiveAssetForWorkflow, getAssetStatsFN, assets_data }) => {
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [viewOption, setviewOption] = useState("Assets");


    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    // assets -> filteredAssets for table
    const filteredAssets = useMemo(() => {
        const list = Array.isArray(assets) ? assets : [];
        const q = searchQuery.trim().toLowerCase();

        return list.filter((asset) => {
            if (statusFilter && asset.status !== statusFilter) return false;

            if (!q) return true;

            const haystack = [
                asset.id && String(asset.id),
                asset.name,
                asset.category,
                asset.location,
                asset.owner_name,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(q);
        });
    }, [assets, searchQuery, statusFilter]);


    useEffect(() => {
        getInventoryFN().finally(() => setIsInitialLoad(false));
    }, [getInventoryFN]);

    useEffect(() => {
        getAssetStatsFN().finally(() => setIsInitialLoad(false));
    }, [getAssetStatsFN]);

    useEffect(() => {
        if (activeassetforworkflow) {
            setSelectedAsset(activeassetforworkflow);
            setIsOverlayOpen(true);
            setActiveAssetForWorkflow(null);
        }
    }, [activeassetforworkflow]);

    const handleAddNew = () => {
        setSelectedAsset(null);
        setIsOverlayOpen(true);
        getAssetStatsFN();
    };

    const handleEdit = (asset) => {
        setSelectedAsset(asset);
        setIsOverlayOpen(true);
        getAssetStatsFN();
    };

    const handleDelete = (assetId) => {
        deleteAssetFN(assetId);
        getAssetStatsFN();
    };


    const handleViewStats = () => {
        setviewOption("Stats");
        getAssetStatsFN();
    };

    const handleViewAssets = () => {
        setviewOption("Assets")
    }


    if (isInitialLoad && loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <p>Loading assets...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-screen text-red-600">
                <p>Error fetching assets: {error}</p>
            </div>
        );
    }



    return (
        <div
            className={`flex flex-col ${sidebar_collapsed ? "w-full" : "w-full"
                } min-h-screen bg-[#F8F8F8]`}
        >
            {/* ASSETS VIEW */}
            {viewOption === "Assets" && (
                <>
                   <div className="px-10 md:px-16 xl:px-24 2xl:px-40 pt-6 pb-10">
                        {/* Header row: title + pill toggle + Add button */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-2xl font-semibold text-[#0E0E0E]">
                                    Asset Management
                                </h1>
                                <p className="text-sm text-gray-500 mt-1">
                                    Track and manage your company assets
                                </p>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    {/* Assets tab */}
                                    <button
                                        onClick={handleViewAssets}
                                        className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Assets"
                                                ? "bg-[#0F0F19] text-white"
                                                : "bg-white text-gray-700 hover:bg-gray-100"
                                            }
    `}
                                    >
                                        {/* Icon */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                        <span>Stats</span>
                                    </button>

                                    {/* Stats tab */}
                                    <button
                                        onClick={handleViewStats}
                                        className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Stats"
                                                ? "bg-[#0F0F19] text-white"
                                                : "bg-white text-gray-700 hover:bg-gray-100"
                                            }
    `}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                        <span>Assets</span>
                                    </button>
                                </div>


                                {/* Smaller Add button */}
                                <button
                                    className="inline-flex items-center gap-1 px-3 h-9 rounded-full bg-[#111827] text-white text-sm font-medium shadow-sm hover:bg-black transition"
                                    onClick={handleAddNew}
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>Add</span>
                                </button>
                            </div>
                        </div>

                        {/* Stat cards row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            {/* Total Assets */}
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center">
                                    <Box className="w-5 h-5 text-[#3B82F6]" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Total Assets</p>
                                    <p className="text-2xl font-semibold text-[#111827] mt-1">
                                        {assets?.length ?? 0}
                                    </p>
                                </div>
                            </div>

                            {/* Total Value */}
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-[#ECFDF5] flex items-center justify-center">
                                    <DollarSign className="w-5 h-5 text-[#10B981]" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Total Value</p>
                                    <p className="text-2xl font-semibold text-[#111827] mt-1">
                                        $
                                        {(assets || [])
                                            .reduce((sum, a) => sum + (Number(a.value) || 0), 0)
                                            .toLocaleString()}
                                    </p>
                                </div>
                            </div>

                            {/* Active */}
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-[#ECFDF5] flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-[#10B981]" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Active</p>
                                    <p className="text-2xl font-semibold text-[#111827] mt-1">
                                        {(assets || []).filter((a) => a.status === "active").length}
                                    </p>
                                </div>
                            </div>

                            {/* In Maintenance */}
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-[#FFF7ED] flex items-center justify-center">
                                    <AlertCircle className="w-5 h-5 text-[#F59E0B]" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">In Maintenance</p>
                                    <p className="text-2xl font-semibold text-[#111827] mt-1">
                                        {(assets || []).filter((a) => a.status === "pending").length}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Search + filter wrapper */}
                        <div className="mb-5">
                            <div className="bg-white rounded-2xl border border-gray-200 px-5 py-3 flex flex-col md:flex-row items-center gap-4">
                                {/* Search */}
                                <div className="flex-1 max-w-[70%]">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search assets..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="
        w-full
        h-9
        pl-9 pr-3
        rounded-md
        bg-[#F3F4F6]
        text-sm text-gray-800
        focus:outline-none
        focus:ring-0
        border-none
      "
                                            style={{
                                                boxShadow: "none",        // remove subtle browser shadows
                                                WebkitAppearance: "none", // consistent look
                                            }}
                                        />

                                        {/* Search icon */}
                                        <svg
                                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                    </div>
                                </div>


                                {/* Status filter */}
                                <div className="w-full  md:w-48">
                                    <select
                                        className="w-full h-11 rounded-full bg-white border border-gray-200 px-3 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="retired">Retired</option>
                                        <option value="pending">In Maintenance</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <div className="min-w-[1100px]">
                                <AssetTable
                                    data={filteredAssets}
                                    totalPagesServer={1}
                                    currentPage2={1}
                                    pageSize2={10}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Overlay */}
                    <Popup
                        open={isOverlayOpen}
                        onClose={() => setIsOverlayOpen(false)}
                        modal
                        nested
                        contentStyle={{
                            width: "80%",
                            maxWidth: "800px",
                            padding: "0",
                            borderRadius: "8px",
                        }}
                    >
                        {(close) => (
                            <AssetOverlay
                                close={() => {
                                    setIsOverlayOpen(false);
                                }}
                                asset={selectedAsset}
                            />
                        )}
                    </Popup>
                </>
            )}

            {/* STATS VIEW */}
            {viewOption === "Stats" && (
              <div className="px-10 md:px-16 xl:px-24 2xl:px-40 pt-6 pb-10 flex flex-col gap-6">

                    {/* Header row */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-semibold text-[#0E0E0E]">
                                Asset Stats
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Visualize asset distribution and performance
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Assets tab */}
                            <button
                                onClick={handleViewAssets}
                                className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Assets"
                                        ? "bg-[#0F0F19] text-white"
                                        : "bg-white text-gray-700 hover:bg-gray-100"
                                    }
    `}
                            >
                                {/* Icon */}
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                                <span>Assets</span>
                            </button>

                            {/* Stats tab */}
                            <button
                                onClick={handleViewStats}
                                className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Stats"
                                        ? "bg-[#0F0F19] text-white"
                                        : "bg-white text-gray-700 hover:bg-gray-100"
                                    }
    `}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                                <span>Stats</span>
                            </button>
                        </div>

                    </div>

                    {/* Top metrics row (4 cards) */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        {/* Donut / category */}
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                            <AssetMetric
                                height={(window.innerHeight - 580) / 2}
                                data={assets_data.data}
                            />
                        </div>

                        {/* Status bar chart */}
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                            <AssetStatusMetric
                                height={(window.innerHeight - 580) / 2}
                                data={assets_data.status}
                            />
                        </div>

                        {/* Quarter trend */}
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                            <AssetQuarterMetric
                                height={(window.innerHeight - 580) / 2}
                                data={assets_data.quarters}
                            />
                        </div>

                        {/* Top assets list */}
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                            <TopAssets
                                height={(window.innerHeight - 580) / 2}
                                assets={assets_data.topAssets}
                            />
                        </div>
                    </div>

                    {/* Map in full-width white card */}
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex-1">
                        <AssetMap rawData={assets_data.location} />
                    </div>
                </div>
            )}
        </div>
    );


};

const mapStateToProps = (state) => {
    console.log("Assets", state.asset)
    return {
        assets: state.asset.assets,
        loading: state.asset.loading,
        error: state.asset.error,
        sidebar_collapsed: state.auth.sidebar_collapsed,
        assets_data: state.asset.assets_stat_data
    }
}

const mapDispatchToProps = (dispatch) => ({
    getInventoryFN: () => dispatch(getInventory()),
    deleteAssetFN: (id) => dispatch(deleteAsset(id)),
    getAssetStatsFN: () => dispatch(getAssetStats()),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(ITInventory);
